/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {translate} from '@ion/core/src/locale/locale';
import {UserContext} from '@ion/core/src/services/MicroServices';
import {
  Entity
} from '@ion/core/src/types/entities';
import {DownloadTemplate} from '@ion/core/src/types/templates';
import {
  exportMapper,
  UserTemplateOptions
} from '@ion/core/src/xlsx/UserTemplate';
import {BulkUploadDialog} from '@ion/dls/src/imports/BulkUploadDialog';
import {useAuthenticatedUser} from '../users/useAuthenticatedUser';
import {getUOM, onImportStart} from './ImportUsersContainerUtils';
import {importAdminUsers} from './state';

// defines the prop definitions for import users container ...
export interface ImportUsersContainerProps {
  onDownload: (params?: DownloadTemplate) => void;
  context?: UserContext;
};

// defines the override styles ...
const overrides = () => ({
  formPaper : {
    minWidth    : '70% !important',
    paddingLeft : '2px !important',
    maxHeight   : '574px',
    height      : '574px'
  }
});

/**
 * This component acts as a container to initiate bulk user import ...
 */
export const ImportUsersContainer = ({
  onDownload,
  context
}: ImportUsersContainerProps) => {
  const dispatch                              = useDispatch();
  const {account}                             = useAuthenticatedUser();
  const itemsRef                              = useRef();
  const importResults                         = useSelector(({importer: {current}}) => current);
  const application                           = useSelector(({initialization: {application}}) => application);
  const [templateOptions, setTemplateOptions] = useState<UserTemplateOptions>({
    groupName : account?.title,
    uom       : Object.values(getUOM())
  });

  function onClose() {
    dispatch(importAdminUsers(false));
  }

  function handleSubmit() {
    onImportStart({
      items : itemsRef.current,
      account,
      application,
      dispatch,
      context
    });
  }

  function itemsUpdate(items) {
    itemsRef.current = items;
  }

  function handleDownload(params?: DownloadTemplate) {
    onDownload?.(params);
  }

  return (
    <BulkUploadDialog open
      dialogTitle={translate('common:editors.import.title',
        {entity: translate('common:entityLabels.users')})}
      onClose={onClose}
      onCancel={onClose}
      styleOverrides={overrides}
      onImport={handleSubmit}
      onDownload={handleDownload}
      onConfirm={onClose}
      exportMapper={exportMapper}
      entity={Entity.user}
      entityLabel={translate('common:entityLabels.users')}
      templateSrcPath={'admin-users'}
      templateOptions={templateOptions}
      isLoading={false}
      itemsUpdate={itemsUpdate}
      isDisabled={false}
      omitActions={false}
      importResults={importResults}
      itemsCount={itemsRef?.current?.length || 0}
      suppressResultsNotification />
  );
};

export default ImportUsersContainer;
